import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { CalendarDays, Plus, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

const StatusPill = ({ s }) => {
  const map = { Pending: "bg-accent/15 text-accent", Approved: "bg-[#2f7d5b]/12 text-[#2f7d5b]", Rejected: "bg-destructive/10 text-destructive" };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[s] || "bg-muted"}`}>{s}</span>;
};

export default function Leaves() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ leave_type: "Casual", from_date: "", to_date: "", reason: "" });

  const load = () => { setLoading(true); api.get("/leaves").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const apply = async () => {
    if (!form.from_date || !form.to_date) { toast.error("Select leave dates"); return; }
    setSaving(true);
    try {
      await api.post("/leaves", { ...form, employee_id: user.id, employee_name: user.name });
      toast.success("Leave request submitted");
      setOpen(false); setForm({ leave_type: "Casual", from_date: "", to_date: "", reason: "" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/leaves/${id}/status`, { status }); toast.success(`Leave ${status.toLowerCase()}`); load(); }
    catch (err) { toast.error("Failed to update"); }
  };

  return (
    <div className="space-y-6" data-testid="leaves-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground ashoka-underline inline-block">Leave Management</h2>
          <p className="text-muted-foreground mt-3">{isAdmin ? "Review and approve leave requests" : "Your leave requests"}</p>
        </div>
        <Button data-testid="apply-leave-button" onClick={() => setOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus className="w-4 h-4 mr-1.5" /> Apply Leave
        </Button>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">No leave requests yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50 text-left">
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Employee</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Type</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden md:table-cell">Dates</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden lg:table-cell">Reason</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                  {isAdmin && <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id} data-testid={`leave-row-${l.id}`} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                    <td className="px-4 py-3 font-medium text-foreground">{l.employee_name}</td>
                    <td className="px-4 py-3">{l.leave_type}</td>
                    <td className="px-4 py-3 hidden md:table-cell font-mono text-xs">{l.from_date} → {l.to_date}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground max-w-xs truncate">{l.reason || "—"}</td>
                    <td className="px-4 py-3"><StatusPill s={l.status} /></td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          {l.status === "Pending" ? (
                            <>
                              <button data-testid={`approve-leave-${l.id}`} onClick={() => setStatus(l.id, "Approved")} className="p-2 rounded-md hover:bg-[#2f7d5b]/10 text-muted-foreground hover:text-[#2f7d5b] transition-colors" title="Approve"><Check className="w-4 h-4" /></button>
                              <button data-testid={`reject-leave-${l.id}`} onClick={() => setStatus(l.id, "Rejected")} className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors" title="Reject"><X className="w-4 h-4" /></button>
                            </>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="leave-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><CalendarDays className="w-5 h-5 text-primary" /> Apply for Leave</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Leave Type</label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Casual">Casual</SelectItem>
                  <SelectItem value="Sick">Sick</SelectItem>
                  <SelectItem value="Earned">Earned</SelectItem>
                  <SelectItem value="Maternity">Maternity</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">From</label>
                <Input data-testid="leave-from-input" type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">To</label>
                <Input data-testid="leave-to-input" type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reason</label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="submit-leave-button" onClick={apply} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
